import type { ReportFilter } from '../../models/filter.types'

// Port of v3 `DataVisible.js:387-407` (`checkReachFrequency`). Reach and frequency are
// de-duplicated audience metrics: the backend can only compute them live, for one channel at a
// time, so a partition offers them ONLY when the request is already narrowed to a channel that
// supports them. Asking for them on a mixed-channel report yields numbers that silently
// double-count people, which is why the channel test is this strict.

/** v3 `DataVisible.js:387` — the only channels whose partitions can serve live reach/frequency. */
const REACH_FREQUENCY_CHANNELS = new Set(['meta_ads', 'tiktok_ads'])

/** v3 `DataVisible.js:389` — the only partitions that carry the metrics at all. */
const REACH_FREQUENCY_PARTITIONS = new Set([
  'budget_allocation',
  'metrics_ad',
  'adgroup_budget_allocation',
])

/**
 * Whether a partition's request is narrowed to a channel that can serve live reach/frequency.
 *
 * The `in` arm's size cap is kept verbatim from v3 (`:400-403`) but is **provably redundant while
 * two channels are supported**: if every distinct value is drawn from a two-element set, the
 * distinct count cannot exceed two, so the `every` check below already subsumes it. Mutation-tested
 * — relaxing the cap alone changes no result. It is retained rather than deleted because it becomes
 * load-bearing the moment a third channel is added, which is exactly when it would be missed.
 */
export function checkReachFrequency(
  partitionName: string | null | undefined,
  filters: readonly ReportFilter[] | null | undefined,
): boolean {
  if (!partitionName || !REACH_FREQUENCY_PARTITIONS.has(partitionName)) return false

  return (filters ?? []).some((filter) => {
    if (filter?.field !== 'marketing_channel') return false

    if (filter.match_mode === 'equals') {
      return typeof filter.value === 'string' && REACH_FREQUENCY_CHANNELS.has(filter.value)
    }

    if (filter.match_mode === 'in' && Array.isArray(filter.value)) {
      const unique = new Set(filter.value)
      return (
        unique.size > 0 &&
        unique.size <= REACH_FREQUENCY_CHANNELS.size &&
        [...unique].every(
          (value) => typeof value === 'string' && REACH_FREQUENCY_CHANNELS.has(value),
        )
      )
    }

    return false
  })
}
